<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['create']);

const FILTER_TYPE_CONTACT = 1;

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('customViews/getUIFlags');
const isCreating = computed(() => uiFlags.value.isCreating);
const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const dialogRef = ref(null);
const agentSearchQuery = ref('');

const state = reactive({
  name: '',
  visibility: 'personal',
  allowedUserIds: [],
});

onMounted(() => {
  store.dispatch('agents/get');
});

const validationRules = {
  name: { required },
};

const v$ = useVuelidate(validationRules, state);

const isShared = computed(() => state.visibility === 'shared');

const filteredAgents = computed(() => {
  const allAgents = agents.value || [];
  const otherAgents = allAgents.filter(
    agent => agent.id !== currentUser.value?.id
  );
  if (!agentSearchQuery.value) return otherAgents;
  const query = agentSearchQuery.value.toLowerCase();
  return otherAgents.filter(
    agent =>
      agent.name?.toLowerCase().includes(query) ||
      agent.email?.toLowerCase().includes(query)
  );
});

const isAgentSelected = agentId => state.allowedUserIds.includes(agentId);

const toggleAgent = agentId => {
  const index = state.allowedUserIds.indexOf(agentId);
  if (index === -1) {
    state.allowedUserIds.push(agentId);
  } else {
    state.allowedUserIds.splice(index, 1);
  }
};

const selectAllAgents = () => {
  const allAgentIds = filteredAgents.value.map(a => a.id);
  state.allowedUserIds = [
    ...new Set([...state.allowedUserIds, ...allAgentIds]),
  ];
};

const deselectAllAgents = () => {
  state.allowedUserIds = [];
};

const allSelected = computed(() => {
  const otherAgents = (agents.value || []).filter(
    a => a.id !== currentUser.value?.id
  );
  return (
    otherAgents.length > 0 && otherAgents.every(a => isAgentSelected(a.id))
  );
});

// Reset allowed users when switching back to personal
watch(
  () => state.visibility,
  newVal => {
    if (newVal === 'personal') {
      state.allowedUserIds = [];
    }
  }
);

const handleDialogConfirm = async () => {
  const isNameValid = await v$.value.$validate();
  if (!isNameValid) return;
  const payload = {
    name: state.name,
    filter_type: FILTER_TYPE_CONTACT,
    visibility: state.visibility,
  };
  if (state.visibility === 'shared') {
    payload.allowed_user_ids = state.allowedUserIds;
  }
  emit('create', payload);
  state.name = '';
  state.visibility = 'personal';
  state.allowedUserIds = [];
  agentSearchQuery.value = '';
  v$.value.$reset();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.TITLE')"
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.CONFIRM')
    "
    :is-loading="isCreating"
    :disable-confirm-button="isCreating"
    @confirm="handleDialogConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="
          t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.LABEL')
        "
        :placeholder="
          t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.PLACEHOLDER')
        "
        :message="
          v$.name.$error
            ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.ERROR')
            : ''
        "
        :message-type="v$.name.$error ? 'error' : 'info'"
      />

      <!-- Visibility toggle -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.LABEL'
            )
          }}
        </label>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-all"
            :class="
              state.visibility === 'personal'
                ? 'bg-n-blue-3 border-n-blue-7 text-n-blue-11'
                : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="state.visibility = 'personal'"
          >
            <i class="i-lucide-lock w-3.5 h-3.5" />
            {{
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.PERSONAL'
              )
            }}
          </button>
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-all"
            :class="
              state.visibility === 'shared'
                ? 'bg-n-blue-3 border-n-blue-7 text-n-blue-11'
                : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="state.visibility = 'shared'"
          >
            <i class="i-lucide-users w-3.5 h-3.5" />
            {{
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.SHARED'
              )
            }}
          </button>
        </div>
      </div>

      <!-- Agent selection (only shown when shared) -->
      <div v-if="isShared" class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.SELECT_AGENTS'
              )
            }}
          </label>
          <button
            type="button"
            class="text-xs text-n-blue-11 hover:text-n-blue-12"
            @click="allSelected ? deselectAllAgents() : selectAllAgents()"
          >
            {{
              allSelected
                ? t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.DESELECT_ALL'
                  )
                : t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.SELECT_ALL'
                  )
            }}
          </button>
        </div>
        <input
          v-model="agentSearchQuery"
          type="text"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.SEARCH_AGENTS'
            )
          "
          class="w-full px-2.5 py-1.5 text-xs rounded-lg bg-n-alpha-2 border border-n-weak focus:border-n-blue-9 focus:ring-1 focus:ring-n-blue-9 focus:outline-none text-n-slate-12 placeholder:text-n-slate-11 transition-all"
          @click.stop
        />
        <div class="max-h-48 overflow-y-auto rounded-lg border border-n-weak">
          <button
            v-for="agent in filteredAgents"
            :key="agent.id"
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-2 transition-all"
            :class="
              isAgentSelected(agent.id) ? 'bg-n-blue-3 hover:bg-n-blue-4' : ''
            "
            @click="toggleAgent(agent.id)"
          >
            <div
              class="flex items-center justify-center w-4 h-4 rounded border transition-all"
              :class="
                isAgentSelected(agent.id)
                  ? 'bg-n-blue-9 border-n-blue-9'
                  : 'border-n-slate-7'
              "
            >
              <i
                v-if="isAgentSelected(agent.id)"
                class="i-lucide-check w-3 h-3 text-white"
              />
            </div>
            <img
              v-if="agent.thumbnail"
              :src="agent.thumbnail"
              :alt="agent.name"
              class="w-5 h-5 rounded-full object-cover"
            />
            <div
              v-else
              class="w-5 h-5 rounded-full bg-n-blue-3 flex items-center justify-center text-xs text-n-blue-11 font-medium"
            >
              {{ agent.name?.charAt(0)?.toUpperCase() }}
            </div>
            <div class="flex flex-col items-start">
              <span class="text-n-slate-12 font-medium">{{ agent.name }}</span>
              <span class="text-xs text-n-slate-11">{{ agent.email }}</span>
            </div>
          </button>
          <div
            v-if="filteredAgents.length === 0"
            class="px-3 py-4 text-sm text-n-slate-11 text-center"
          >
            {{
              t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.NO_AGENTS'
              )
            }}
          </div>
        </div>
        <p
          v-if="state.allowedUserIds.length > 0"
          class="text-xs text-n-slate-11"
        >
          {{
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.VISIBILITY.SELECTED_COUNT',
              {
                count: state.allowedUserIds.length,
              }
            )
          }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
